/**
 * `usePresentationDirector` — the React adapter for the {@link PresentationDirector}
 * It owns, for the lifetime of a mounted map:
 *   - a stable **node registry** (`pixiCombatBoard.ts`'s `setTokens` registers a token's Pixi
 *     `Container`, the animator tweens it);
 *   - a stable Pixi {@link makePixiAnimator} over that registry (the renderer-coupled seam), built
 *     SYNCHRONOUSLY before `mountCombatBoard`'s async `Application` exists — so it's handed a live
 *     {@link TickerRef} accessor (`{ get: () => tickerRef.current }`) rather than a `Ticker` itself;
 *     `setTicker` (below) populates `tickerRef` once the board mounts;
 *   - a stable {@link PresentationDirector} built once over those;
 *   - the ingest effect: feed each authoritative `EncounterState` to the Director, but **snap**
 *     (don't diff) on the welcome seed and every reconnect so a client never replays the backlog
 *     it missed while away.
 *
 * It exposes the lagging `presentation` the map renders, the register/unregister/setTicker calls
 * `CombatStage.tsx`/`pixiCombatBoard.ts` wire to a token's container / the mounted `Application`, and
 * `commitLocalMove` (the optimistic own-drag dedup).
 *
 * `planMove` closes over live authoritative state, so it's read through a ref — the Director is
 * built once and must not be reconstructed when the closure's identity changes each render.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSyncExternalStore } from "react";
import type { Container, Ticker } from "pixi.js";
import type { Cell, EncounterState, ResolvedStep } from "@lancer-vtt/domain";
import type { ConnStatus } from "../roomWs";
import type { AttackOutcomeItem, AttackTravelItem } from "./attackFxItems";
import { PresentationDirector, type HoldView, type MovePlanner, type FloatTone } from "./presentationDirector";
import { awaitResolutionThrow, registerResolutionThrow } from "../dice/resolutionThrows";
import { throwSteps } from "../dice/diceController";
import { NO_HOLDS, holdKey, reactionThrowKey, segmentThrowKey, type ChainSegment } from "./chainSegments";
import { holdOpens } from "./sequenceHolds";
import {
  makePixiAnimator,
  type FloatSink,
  type ProjectileSink,
  type ImpactSink,
  type FocusSink,
  MOVE_STEP_MS,
  FLOURISH_MS,
  FLOAT_PACING_MS,
  FOLLOW_ON_FLOAT_PACING_MS,
} from "./pixiAnimator";

/** A live floating-combat-text item the map renders — one transient `<DamageFloat>` per beat. */
export type FloatItem = { key: number; x: number; y: number; text: string; tone: FloatTone };

/** An attack's travel the map draws (`attack-animations` 3.1), handed to `pixiCombatBoard.ts`'s
 *  `spawnProjectile` like a {@link FloatItem}; or `clear`, which drops every live attack look (a snap). */
export type ProjectileItem = { key: number; attack: AttackTravelItem } | { key: number; clear: true };

/** A live impact-burst item the map renders — one transient `<ImpactBurst>` per beat
 *  the same shape/lifecycle as {@link ProjectileItem}. `tech`
 *  recolors the SAME pooled burst hacker-green for a quick-tech
 *  flourish (a `tech_burst` beat) instead of the ordinary ballistic orange. */
export type ImpactItem = { key: number; x: number; y: number; tech?: boolean } | { key: number; attack: AttackOutcomeItem };

/**
 * The resolution rider this hook routes through the event path: the ordered steps, the monotonic
 * `seq` (a fresh resolution always bumps it), and `kind` —
 * narrowed from the controller's `BroadcastResolution`. When `seq` advances, the matching
 * `encounter_change` is resolution-driven, so we compile its steps into beats instead of diffing.
 *
 * `kind` is `ApplyResolvedCommand.kind` (`"attack"` for every weapon/tech/grapple/ram/improvised
 * attack, `"action"` for a Scan/Lock On/Bolster/other granted-action activate, or one of a handful
 * of trigger-kind values — `"burn_tick"`, `"lifecycle_trigger"`, …) — a resolution-*shape*
 * discriminant, NOT a per-action id. It was already broadcast server-authoritatively and already
 * flows into `RemoteEncounterController.latestResolution` (pinned by
 * `remoteEncounterController.test.ts`); this widening is the first frontend-internal consumer past
 * that controller. `compileResolution` uses it only as a defensive scope on top of the REAL
 * tech-vs-ballistic signal, which lives in the steps themselves (a `to_hit`'s `vs: "edef"` combined
 * with no `damage` step in its run, or a `status_add`'s `cause: "tech"` — see its doc for why `kind`
 * alone can't carry this: it's shared by every attack shape, tech or not, and by every granted
 * action, tech or not).
 */
/** `id` is the SERVER-stamped resolution id (`dice-land-before-reveal` 3.1) — the correlation key
 *  the reveal gate matches a dice throw on. `seq` stays the client-local "is this new" cut. */
export type ResolutionRider = { steps: ResolvedStep[]; areaOrigin?: Cell; kind?: string; id: string; seq: number; holds?: { checks: boolean; start: boolean } };

export type UsePresentationDirector = {
  /** The lagging shadow state the map renders (`null` only before the first seed). */
  presentation: EncounterState | null;
  /** `pixiCombatBoard.ts`'s `setTokens` registers a token's Pixi `Container` here on every rebuild
   *  so the animator can tween it by id (Pixi tokens have no persistent per-token React
   *  component, so the registration lives at the point Pixi actually builds the container, not a
   *  mount effect). */
  registerNode: (id: string, node: Container) => void;
  /** Clears a token's registration (called right before `setTokens` rebuilds, for every previously
   *  registered id). */
  unregisterNode: (id: string) => void;
  /** Populate the animator's live {@link TickerRef} once `mountCombatBoard`'s async `Application`
   *  resolves — `CombatStage.tsx`'s mount effect calls this
   *  with `handle.app.ticker` in the SAME `.then(handle)` that sets `boardHandleRef`, and with `null`
   *  on unmount. Before this is ever called, `pixiAnimator.ts`'s tweens fall back to a bare
   *  `requestAnimationFrame` loop (never actually exercised — nothing animates before the board mounts). */
  setTicker: (ticker: Ticker | null) => void;
  /**
   * Read a token's live Pixi `Container` by id (or `undefined` if none is registered). The
   * container is what the animator tweens / the live drag moves imperatively, so it carries the
   * LIVE position the lagging `pos` doesn't.
   */
  getNode: (id: string) => Container | undefined;
  /** Commit a locally-accepted move to presentation immediately (own-drag dedup, no re-walk). */
  commitLocalMove: (id: string, pos: Cell) => void;
  /**
   * Register interest in combatant `id`'s next terminal move resolution — the drag-ghost's teardown
   * seam (see {@link PresentationDirector.watchSettle}'s
   * doc for the full terminal-shape enumeration). Returns an unsubscribe.
   */
  watchSettle: (id: string, onSettle: () => void) => () => void;
  /**
   * Mark combatant `id`'s move as outstanding (`true`) or resolved (`false`) — the drag-ghost's
   * hold seam (see {@link PresentationDirector.noteMoveHeld}'s doc).
   * `CombatStage`'s `finishTokenDrag` calls this for the WHOLE window a PLAY move is in flight (from
   * the accepted drop through its own terminal resolution), not only during a checkpoint hold — so
   * `watchSettle`'s teardown never fires a beat early off an unrelated reconcile.
   */
  noteMoveHeld: (id: string, held: boolean) => void;
  /** The live floating-combat-text items the map's float layer renders. */
  floats: FloatItem[];
  /** A `<DamageFloat>` calls this when its rise/fade tween finishes, so the host drops it. */
  onFloatDone: (key: number) => void;
  /** The live projectile-trail items the map's projectile layer renders. */
  projectiles: ProjectileItem[];
  /** A `<ProjectileFlourish>` calls this when its travel/fade tween finishes, so the host drops it. */
  onProjectileDone: (key: number) => void;
  /** The live impact-burst items the map's impact layer renders. */
  impacts: ImpactItem[];
  /** An `<ImpactBurst>` calls this when its expand/fade tween finishes, so the host drops it. */
  onImpactDone: (key: number) => void;
  /** The tokens the chain link now playing rings: its actor and target, `[]` outside a chain (`chained-resolution-sequence` 2.2). */
  focus: readonly string[];
  /** The held roll now waiting on Roll it, or `null` (`chained-resolution-sequence` 3.2): the stakes banner reads it. */
  hold: HoldView | null;
  /** Whether a held roll is playing or still queued: read-only vitals displays read `presentation` while it is (3.3). */
  holdAhead: boolean;
  /** Is a beat playing or queued? Drives the transient "skip animations" affordance. */
  isAnimating: boolean;
  /** User skip → `snap` to current (hard-snap). Safe to call when nothing is animating. */
  skip: () => void;
};

/** The live encounter feed the Director ingests — the authoritative state, the resolution rider that
 *  rides a resolution-bearing change (or `null`), and the connection status (a reconnect → `snap`). */
export type DirectorFeed = {
  authoritative: EncounterState | null;
  latestResolution: ResolutionRider | null;
  connStatus: ConnStatus;
};

export function usePresentationDirector(feed: DirectorFeed, planMove: MovePlanner, cellPx: number): UsePresentationDirector {
  const { authoritative, latestResolution, connStatus } = feed;
  // `planMove` is rebuilt each render (it closes over board state); read it through a ref so the
  // Director — built ONCE below — always calls the latest without being reconstructed.
  const planMoveRef = useRef(planMove);
  planMoveRef.current = planMove;

  // Stable singletons for this mounted map (lazy-init refs so identity survives every re-render).
  const registryRef = useRef<Map<string, Container> | null>(null);
  if (registryRef.current === null) registryRef.current = new Map();
  const registry = registryRef.current;

  // The animator's live ticker handle — `null` until `CombatStage.tsx`'s mount effect
  // calls `setTicker` (below) with the mounted board's `app.ticker`. This hook builds the animator
  // SYNCHRONOUSLY on first render, before that `Application` exists, so `makePixiAnimator` is handed a
  // `{ get: () => tickerRef.current }` accessor rather than the ticker itself — the animator reads it
  // fresh on every tween/shake call, well after this ref gets populated.
  const tickerRef = useRef<Ticker | null>(null);

  // Floating-combat-text the event path spawns. A React-state queue-bridge: the animator's
  // `float` sink pushes an item here, `CombatStage.tsx` hands it to `pixiCombatBoard.ts`'s
  // `spawnFloat` and immediately drops it via `onFloatDone` (never rendered via JSX). `setFloats` is
  // identity-stable, so the sink built once below always drives the latest state.
  const [floats, setFloats] = useState<FloatItem[]>([]);
  const floatKeyRef = useRef(0);
  const floatSinkRef = useRef<FloatSink | null>(null);
  if (floatSinkRef.current === null) {
    floatSinkRef.current = {
      spawn: ({ x, y, spec }) => {
        const key = (floatKeyRef.current += 1);
        setFloats((f) => [...f, { key, x, y, text: spec.text, tone: spec.tone }]);
      },
      clear: () => setFloats([]),
    };
  }

  // Projectile trails the event path spawns — the same React-state
  // queue-bridge pattern as `floats` above (each item is handed to `pixiCombatBoard.ts`'s
  // `spawnProjectile` and immediately dropped, never rendered via JSX), so a stable `setProjectiles`
  // identity lets the sink built once below always drive latest state.
  const [projectiles, setProjectiles] = useState<ProjectileItem[]>([]);
  const projectileKeyRef = useRef(0);
  const projectileSinkRef = useRef<ProjectileSink | null>(null);
  if (projectileSinkRef.current === null) {
    projectileSinkRef.current = {
      spawn: (attack) => {
        const key = (projectileKeyRef.current += 1);
        setProjectiles((p) => [...p, { key, attack }]);
      },
      // A snap replaces the queue with one `clear`, so the board also drops the looks already
      // playing (a swarm's drones and a Smart reticle hold over their target through the dice).
      clear: () => {
        const key = (projectileKeyRef.current += 1);
        setProjectiles([{ key, clear: true }]);
      },
    };
  }

  // Impact bursts the event path spawns — the same React-state
  // queue-bridge pattern as `projectiles`/`floats` above (each item handed to `pixiCombatBoard.ts`'s
  // `spawnImpact` and immediately dropped, never rendered via JSX), so a stable `setImpacts` identity
  // lets the sink built once below drive latest state.
  const [impacts, setImpacts] = useState<ImpactItem[]>([]);
  const impactKeyRef = useRef(0);
  const impactSinkRef = useRef<ImpactSink | null>(null);
  if (impactSinkRef.current === null) {
    impactSinkRef.current = {
      spawn: (item) => {
        const key = (impactKeyRef.current += 1);
        setImpacts((p) => [...p, { key, ...item }]);
      },
      clear: () => setImpacts([]),
    };
  }

  // The chain link's focus ring (`chained-resolution-sequence` 2.2): standing state rather than a
  // queue-bridge, since the ring stays up while its link plays. `CombatStage` rings these ids and
  // pans to the first when it is off screen.
  const [focus, setFocus] = useState<readonly string[]>([]);
  const focusSinkRef = useRef<FocusSink | null>(null);
  if (focusSinkRef.current === null) focusSinkRef.current = { set: (ids) => setFocus((prev) => (sameIds(prev, ids) ? prev : [...ids])) };

  const directorRef = useRef<PresentationDirector | null>(null);
  if (directorRef.current === null) {
    const animator = makePixiAnimator(
      { get: (id) => registry.get(id) },
      { float: floatSinkRef.current, projectile: projectileSinkRef.current, impact: impactSinkRef.current, focus: focusSinkRef.current },
      { cellPx, ticker: { get: () => tickerRef.current } },
    );
    // Inject the live animator timings so the backpressure estimate matches what the
    // animator actually plays — the Director core stays renderer-agnostic, the constants live here.
    directorRef.current = new PresentationDirector(
      animator,
      (id, from, to) => planMoveRef.current(id, from, to),
      { moveStepMs: MOVE_STEP_MS, flourishMs: FLOURISH_MS, floatMs: FLOAT_PACING_MS, followOnFloatMs: FOLLOW_ON_FLOAT_PACING_MS },
    );
  }
  const director = directorRef.current;

  // Feed authoritative changes into the Director, routing by **provenance**:
  //   - reconnect / welcome seed → `snap` (jump to current; never replay the missed backlog).
  //   - an `encounter_change` carrying a NEW resolution (its `seq` advanced) → the EVENT path:
  //     compile its steps into beats. The diff for that message is ignored — running both would
  //     double-animate (`applyResolved(prev, steps) === next` makes the diff redundant).
  //   - any other `encounter_change` → the DIFF path (voluntary move / GM fiat / editor).
  // The two are mutually exclusive per message; the `seq` is the cut.
  const prevConn = useRef<ConnStatus>(connStatus);
  const lastResSeq = useRef<number | null>(null);
  useEffect(() => {
    const reconnected = prevConn.current !== "connected" && connStatus === "connected";
    prevConn.current = connStatus;
    if (authoritative === null) return;
    if (reconnected) {
      director.snap(authoritative);
      lastResSeq.current = latestResolution?.seq ?? lastResSeq.current; // don't replay a pre-reconnect resolution
      return;
    }
    const seq = latestResolution?.seq ?? null;
    if (seq !== null && seq !== lastResSeq.current) {
      lastResSeq.current = seq;
      // `diceWait` is the reveal gate: the board plays the shot
      // and the burst on time, then holds the damage/MISS float until this resolution's own dice
      // have stopped rolling. The key is the SERVER-stamped resolution id — the same key
      // `CombatApp`'s dice effect registers its throw under, one component up, and the
      // same key COMMS.app holds its result card on. Effect order between them doesn't matter;
      // `awaitResolutionThrow` is a rendezvous, not a poll.
      //
      // A chained resolution's later segments (`chained-resolution-sequence` 2.1) throw their own
      // dice when the board reaches them, under `id#n`, so each link's dice land with its link.
      //
      // A held roll (3.1) waits on its release latch; the segment's dice gate behind it throws the
      // held die only once the hold opens. A held primary action throws its own dice the same way.
      const id = latestResolution!.id;
      director.ingestResolution(latestResolution!.steps, authoritative, {
        areaOrigin: latestResolution!.areaOrigin,
        kind: latestResolution!.kind,
        holds: latestResolution!.holds ?? NO_HOLDS,
        diceWait: (segment) => (segment.index === 0 && segment.hold === undefined ? () => awaitResolutionThrow(segmentThrowKey(id, 0)) : () => throwSegment(id, segment)),
        reactionDiceWait: (reaction) => () => {
          const key = reactionThrowKey(id, reaction.index);
          registerResolutionThrow(key, throwSteps(reaction.steps));
          return awaitResolutionThrow(key);
        },
        holdWait: (segment) => {
          const key = holdKey(id, segment.index);
          return { key, wait: () => holdOpens(key) };
        },
      });
    } else {
      director.ingest(authoritative);
    }
  }, [authoritative, latestResolution, connStatus, director]);

  const subscribe = useMemo(() => (cb: () => void) => director.subscribe(cb), [director]);
  const presentation = useSyncExternalStore(subscribe, () => director.getSnapshot());
  // The same external store, read for the queue's activity (a primitive, so no spurious re-renders):
  // the Director publishes when the queue settles, so this flips false the moment the last beat ends.
  const isAnimating = useSyncExternalStore(subscribe, () => director.isAnimating());
  // The held roll now waiting on Roll it (the stakes banner), and whether one is still ahead (the
  // vitals read-swap, 3.3).
  const hold = useSyncExternalStore(subscribe, () => director.getHold());
  const holdAhead = useSyncExternalStore(subscribe, () => director.hasHoldAhead());

  const actions = useMemo(
    () => ({
      registerNode: (id: string, node: Container) => {
        registry.set(id, node);
      },
      unregisterNode: (id: string) => {
        registry.delete(id);
      },
      getNode: (id: string) => registry.get(id),
      setTicker: (t: Ticker | null) => {
        tickerRef.current = t;
      },
      commitLocalMove: (id: string, pos: Cell) => director.commitLocalMove(id, pos),
      watchSettle: (id: string, onSettle: () => void) => director.watchSettle(id, onSettle),
      noteMoveHeld: (id: string, held: boolean) => director.noteMoveHeld(id, held),
      onFloatDone: (key: number) => setFloats((f) => f.filter((x) => x.key !== key)),
      onProjectileDone: (key: number) => setProjectiles((p) => p.filter((x) => x.key !== key)),
      onImpactDone: (key: number) => setImpacts((p) => p.filter((x) => x.key !== key)),
      skip: () => director.skip(),
    }),
    [director, registry],
  );

  return { presentation, floats, projectiles, impacts, focus, hold, holdAhead, isAnimating, ...actions };
}

/** Two id lists name the same tokens in the same order. */
function sameIds(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id, i) => id === b[i]);
}

/**
 * Throw a later chain segment's dice now, register the throw under its key, and wait for it to
 * settle (with the registry's ceiling, so a throw that never reports cannot wedge the board).
 */
function throwSegment(id: string, segment: ChainSegment): Promise<void> {
  const key = segmentThrowKey(id, segment.index);
  registerResolutionThrow(key, throwSteps(segment.steps));
  return awaitResolutionThrow(key);
}
